import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Stars as StarsIcon,
  Home as HomeIcon,
  Diamond as DiamondIcon,
  Favorite as FavoriteIcon,
  Schedule as ScheduleIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';

import { Service, servicesApi } from '../services/api';

const Services: React.FC = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await servicesApi.getAll();
      setServices(data);
    } catch (err) {
      setError('Failed to load services. Please try again later.');
      console.error('Error fetching services:', err);
    } finally {
      setLoading(false);
    }
  };

  const getServiceIcon = (name: string) => {
    const iconProps = { fontSize: 36 as const, color: '#C8922A' };
    if (name.toLowerCase().includes('horoscope')) return <StarsIcon sx={iconProps} />;
    if (name.toLowerCase().includes('vastu')) return <HomeIcon sx={iconProps} />;
    if (name.toLowerCase().includes('gemstone')) return <DiamondIcon sx={iconProps} />;
    if (name.toLowerCase().includes('marriage') || name.toLowerCase().includes('compatibility'))
      return <FavoriteIcon sx={iconProps} />;
    if (name.toLowerCase().includes('consultation')) return <PsychologyIcon sx={iconProps} />;
    return <ScheduleIcon sx={iconProps} />;
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(price);

  const formatDuration = (minutes: number) => {
    if (minutes >= 60) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return m === 0 ? `${h} hour${h > 1 ? 's' : ''}` : `${h}h ${m}m`;
    }
    return `${minutes} minutes`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 }, pt: { xs: 2, md: 3 } }}>
        <Typography variant="h1" sx={{ mb: 2 }}>Our Services</Typography>
        <Typography variant="h5" sx={{ color: 'text.secondary', maxWidth: 500, mx: 'auto' }}>
          Comprehensive astrology services to guide you through life's journey
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button onClick={fetchServices} sx={{ ml: 2 }}>Retry</Button>
        </Alert>
      )}

      {/* Services Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 5 }}>
        {services.map((service) => (
          <Card key={service.id} sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'visible' }}>
            {/* Popular badge for horoscope/consultation services */}
            {(service.name.toLowerCase().includes('horoscope') || service.name.toLowerCase().includes('consultation')) && services.indexOf(service) === 0 && (
              <Chip
                label="⭐ Popular"
                size="small"
                sx={{
                  position: 'absolute',
                  top: -10,
                  right: 16,
                  backgroundColor: 'secondary.main',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                }}
              />
            )}
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>{getServiceIcon(service.name)}</Box>
              <Typography variant="h4" sx={{ mb: 1.5, textAlign: 'center' }}>{service.name}</Typography>
              <Typography variant="body1" sx={{ mb: 2, flex: 1 }}>{service.description}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip label={formatDuration(service.duration_minutes)} color="secondary" variant="outlined" size="small" />
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>{formatPrice(service.price)}</Typography>
              </Box>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/book-slot', { state: { selectedService: service } })}
              >
                Book This Service
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Benefits */}
      <Card sx={{ p: { xs: 2, md: 3 }, mb: 4, backgroundColor: '#FFF5EB' }}>
        <CardContent>
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 3 }}>What You Get With Every Consultation</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
            {[
              { emoji: '📊', title: 'Detailed Analysis', text: 'In-depth birth chart and planetary position examination' },
              { emoji: '🎯', title: 'Practical Solutions', text: 'Actionable advice and remedies for your situation' },
              { emoji: '📞', title: 'Follow-up Support', text: 'One free follow-up call within 30 days' },
            ].map((b) => (
              <Box key={b.title} sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>{b.emoji} {b.title}</Typography>
                <Typography variant="body2">{b.text}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* CTA */}
      <Card sx={{ p: { xs: 3, md: 4 }, textAlign: 'center', backgroundColor: '#FFF5EB' }}>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 2 }}>Ready to Get Started?</Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Choose a service and book your consultation slot today.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" onClick={() => navigate('/book-slot')}>Book Consultation</Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/contact')}>Contact Us</Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Services;
